/* No network work: fit the title and highlight the section currently in view. */
(() => {
  const instances = new Map();

  function setupTitles(page) {
    const titles = Array.from(page.querySelectorAll('.listing-heading__main h1'));
    if (!titles.length) return () => {};
    let frame = 0;
    let disposed = false;
    let forceNext = false;
    const measurements = new WeakMap();
    const fitTitles = () => {
      frame = 0;
      const force = forceNext;
      forceNext = false;
      titles.forEach((title) => {
        if (!title.isConnected) return;
        const styles = getComputedStyle(title);
        const available = title.clientWidth - (parseFloat(styles.paddingLeft) || 0) - (parseFloat(styles.paddingRight) || 0);
        if (available <= 0) return;
        const minimum = Math.max(14, parseFloat(styles.getPropertyValue('--rh-title-min-size')) || 16);
        const maximum = Math.max(minimum, parseFloat(styles.getPropertyValue('--rh-title-max-size')) || 32);
        const signature = [available, maximum, minimum, title.textContent, styles.fontFamily, styles.fontWeight, styles.letterSpacing].join('|');
        if (!force && measurements.get(title) === signature) return;
        measurements.set(title, signature);
        const range = document.createRange();
        range.selectNodeContents(title);
        const setSize = (size) => title.style.setProperty('--rh-title-size', size + 'px');
        setSize(maximum);
        if (range.getBoundingClientRect().width <= available) return;
        setSize(minimum);
        // Keep exceptionally long titles readable; CSS provides horizontal scrolling at this floor.
        if (range.getBoundingClientRect().width >= available) return;
        let low = minimum;
        let high = maximum;
        for (let step = 0; step < 8; step += 1) {
          const middle = (low + high) / 2;
          setSize(middle);
          if (range.getBoundingClientRect().width <= available) low = middle;
          else high = middle;
        }
        setSize(Math.floor(low * 10) / 10);
      });
    };
    const schedule = (force = false) => {
      if (disposed) return;
      forceNext = forceNext || force === true;
      if (!frame) frame = requestAnimationFrame(fitTitles);
    };
    const resize = 'ResizeObserver' in window ? new ResizeObserver(() => schedule()) : null;
    titles.forEach((title) => resize?.observe(title));
    const refresh = () => schedule();
    const fontsLoaded = () => schedule(true);
    window.addEventListener('resize', refresh, { passive: true });
    window.addEventListener('orientationchange', refresh, { passive: true });
    document.fonts?.ready.then(fontsLoaded);
    document.fonts?.addEventListener('loadingdone', fontsLoaded);
    fitTitles();
    return () => {
      disposed = true;
      if (frame) cancelAnimationFrame(frame);
      resize?.disconnect();
      window.removeEventListener('resize', refresh);
      window.removeEventListener('orientationchange', refresh);
      document.fonts?.removeEventListener('loadingdone', fontsLoaded);
      titles.forEach((title) => title.style.removeProperty('--rh-title-size'));
    };
  }

  function setupNavigation(page) {
    if (!('IntersectionObserver' in window)) return () => {};
    const links = Array.from(page.querySelectorAll('[data-product-navigation] a[href^="#"]'));
    const sections = links.map((link) => document.getElementById(link.hash.slice(1))).filter(Boolean);
    if (!sections.length) return () => {};
    const visible = new Set();
    const header = document.querySelector('.rh-header-section');
    const navigation = page.querySelector('.listing-widget--navigation[data-listing-area="top"]');
    let observer = null;
    let measuredOffset = -1;
    const activate = (id) => links.forEach((link) => {
      if (link.hash === '#' + id) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
    const measureNavigation = () => {
      const navHeight = navigation ? Math.ceil(navigation.getBoundingClientRect().height) : 0;
      page.style.setProperty('--rh-product-nav-height', navHeight + 'px');
      const offset = Math.ceil(header?.getBoundingClientRect().height || 0) + navHeight + 12;
      if (offset === measuredOffset) return;
      measuredOffset = offset;
      observer?.disconnect();
      visible.clear();
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        const current = sections.find((section) => visible.has(section));
        if (current) activate(current.id);
      }, { rootMargin: '-' + offset + 'px 0px -30% 0px', threshold: 0 });
      sections.forEach((section) => observer.observe(section));
    };
    measureNavigation();
    const resize = 'ResizeObserver' in window ? new ResizeObserver(measureNavigation) : null;
    if (navigation) resize?.observe(navigation);
    if (header) resize?.observe(header);
    if (!resize) window.addEventListener('resize', measureNavigation, { passive: true });
    const click = (event) => {
      const link = event.target.closest('[data-product-navigation] a');
      if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey) activate(link.hash.slice(1));
    };
    page.addEventListener('click', click);
    return () => {
      observer?.disconnect();
      resize?.disconnect();
      window.removeEventListener('resize', measureNavigation);
      page.removeEventListener('click', click);
    };
  }

  function setup(scope = document) {
    const pages = Array.from(scope.querySelectorAll('[data-product-experience]'));
    if (scope.matches?.('[data-product-experience]')) pages.unshift(scope);
    pages.forEach((page) => {
      if (instances.has(page)) return;
      const cleanupTitles = setupTitles(page);
      const cleanupNavigation = setupNavigation(page);
      instances.set(page, () => {
        cleanupTitles();
        cleanupNavigation();
      });
    });
  }
  setup();
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((cleanup, page) => {
      if (event.target.contains(page)) { cleanup(); instances.delete(page); }
    });
  });
})();
